// webapp/src/components/feedback/FeedbackReviewStep.tsx
import { useRef, useState, type ChangeEvent } from 'react';
import {
  makeStyles,
  tokens,
  Button,
  Input,
  Spinner,
  Field,
} from '@fluentui/react-components';
import {
  EditRegular,
  ChevronDownRegular,
  ChevronUpRegular,
  ArrowSyncRegular,
  ImageRegular,
} from '@fluentui/react-icons';

import type {
  WizardState,
  FeedbackEnvironment,
  FeedbackScreenshots,
} from '../../services/feedbackService';
import { readImageFileAsBase64 } from '../../services/screenshotService';
import type { FeedbackType } from './FeedbackWizard';

import { BUG_STEPS, FEATURE_STEPS } from './FeedbackWizardSteps';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FeedbackReviewStepProps {
  wizardState: WizardState;
  environment: FeedbackEnvironment;
  screenshots: FeedbackScreenshots;
  capturing: boolean;
  onRecapture: () => void;
  onEditStep: (stepIndex: number) => void;
  onTitleChange: (title: string) => void;
  /** Reporter identity (prefilled from the signed-in account, editable). */
  reporterName?: string;
  reporterEmail?: string;
  onReporterNameChange?: (name: string) => void;
  onReporterEmailChange?: (email: string) => void;
  /** Bug flow only: attaches a user-picked image as the screenshot. */
  onAttachImage?: (base64: string) => void;
  type: FeedbackType;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  answerCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '12px',
    borderRadius: '8px',
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  answerContent: {
    flex: 1,
    minWidth: 0,
  },
  answerLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
    marginBottom: '4px',
  },
  answerText: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  skippedText: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  editButton: {
    minHeight: '44px',
    minWidth: '44px',
    flexShrink: 0,
  },
  screenshotRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: '8px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  thumbnail: {
    width: '80px',
    height: '60px',
    objectFit: 'cover',
    borderRadius: '4px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  expandableHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    borderRadius: '8px',
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    cursor: 'pointer',
    minHeight: '44px',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground2Hover,
    },
  },
  expandableContent: {
    padding: '12px',
    borderRadius: '0 0 8px 8px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderLeft: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRight: `1px solid ${tokens.colorNeutralStroke1}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    marginTop: '-1px',
  },
  envTable: {
    width: '100%',
    fontSize: '13px',
    borderCollapse: 'collapse',
  },
  envRow: {
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  envLabel: {
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
    padding: '4px 8px 4px 0',
    whiteSpace: 'nowrap',
    verticalAlign: 'top',
  },
  envValue: {
    color: tokens.colorNeutralForeground1,
    padding: '4px 0',
    wordBreak: 'break-word',
  },
  sectionLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
    marginBottom: '8px',
  },
  spinnerBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
});

// ---------------------------------------------------------------------------
// Severity label mapping
// ---------------------------------------------------------------------------

const SEVERITY_LABELS: Record<string, string> = {
  critical: 'Blocks my work',
  medium: 'Annoying but I can work around it',
  low: 'Minor cosmetic issue',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FeedbackReviewStep({
  wizardState,
  environment,
  screenshots,
  capturing,
  onRecapture,
  onEditStep,
  onTitleChange,
  reporterName = '',
  reporterEmail = '',
  onReporterNameChange,
  onReporterEmailChange,
  onAttachImage,
  type,
}: FeedbackReviewStepProps) {
  const styles = useStyles();
  const [showEnvDetails, setShowEnvDetails] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [attachingImage, setAttachingImage] = useState(false);
  const [attachError, setAttachError] = useState('');

  // Bug flow only: attach an image already on the device (the wizard's live
  // capture cannot photograph errors that appear inside the feedback UI).
  const handleAttachFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset so picking the same file again still fires onChange.
    event.target.value = '';
    if (!file || !onAttachImage) return;
    setAttachError('');
    setAttachingImage(true);
    try {
      onAttachImage(await readImageFileAsBase64(file));
    } catch (err) {
      setAttachError(err instanceof Error ? err.message : 'Could not attach the image.');
    } finally {
      setAttachingImage(false);
    }
  };

  const textSteps = type === 'bug' ? BUG_STEPS : FEATURE_STEPS;

  // Determine auto-generated title
  const autoTitle = type === 'bug'
    ? (wizardState.whatHappened || '').substring(0, 100)
    : (wizardState.goal || '').substring(0, 100);
  const displayTitle = wizardState.customTitle || autoTitle;

  // Build answer cards
  const answerCards: Array<{
    label: string;
    value: string | undefined;
    stepIndex: number;
    isOptional?: boolean;
  }> = [];

  textSteps.forEach((step, index) => {
    answerCards.push({
      label: step.title,
      value: wizardState[step.fieldKey] as string | undefined,
      stepIndex: index,
      isOptional: step.isOptional,
    });
  });

  // Bug: severity card (step index 3)
  if (type === 'bug') {
    answerCards.push({
      label: 'Severity',
      value: wizardState.severity ? SEVERITY_LABELS[wizardState.severity] : undefined,
      stepIndex: 3,
    });
  }

  // Environment detail rows
  const envRows: Array<{ label: string; value: string }> = [
    { label: 'App Version', value: environment.appVersion },
    { label: 'Browser', value: environment.browser },
    { label: 'OS', value: environment.os },
    { label: 'Screen Size', value: environment.screenSize },
    { label: 'Device Type', value: environment.deviceType },
    { label: 'Route', value: environment.route },
  ];

  if (environment.projectName) {
    envRows.push({ label: 'Project', value: environment.projectName });
  }
  if (environment.floorName) {
    envRows.push({ label: 'Floor', value: environment.floorName });
  }
  if (environment.deviceCount !== undefined) {
    envRows.push({ label: 'Device Count', value: String(environment.deviceCount) });
  }
  if (environment.activeTab) {
    envRows.push({ label: 'Active Tab', value: environment.activeTab });
  }

  return (
    <div className={styles.container}>
      {/* Editable title */}
      <Field label="Issue Title">
        <Input
          value={displayTitle}
          onChange={(_e, data) => onTitleChange(data.value)}
          maxLength={200}
          aria-label="Issue Title"
          data-testid="issue-title-input"
        />
      </Field>

      {/* Reporter identity — plain editable inputs (no paste handlers), so
          paste and iOS Safari long-press paste work; autocomplete/inputMode
          give the right keyboard + autofill. */}
      <Field label="Your name">
        <Input
          value={reporterName}
          onChange={(_e, data) => onReporterNameChange?.(data.value)}
          autoComplete="name"
          maxLength={100}
          aria-label="Reporter name"
          data-testid="reporter-name-input"
        />
      </Field>
      <Field label="Your email">
        <Input
          value={reporterEmail}
          onChange={(_e, data) => onReporterEmailChange?.(data.value)}
          type="email"
          inputMode="email"
          autoComplete="email"
          maxLength={200}
          aria-label="Reporter email"
          data-testid="reporter-email-input"
        />
      </Field>

      {/* Answer cards */}
      <div className={styles.sectionLabel}>Your answers</div>
      {answerCards.map((card) => (
        <div key={card.stepIndex} className={styles.answerCard}>
          <div className={styles.answerContent}>
            <div className={styles.answerLabel}>{card.label}</div>
            {card.value ? (
              <div className={styles.answerText}>{card.value}</div>
            ) : (
              <div className={styles.skippedText}>
                {card.isOptional ? 'Skipped' : 'Not provided'}
              </div>
            )}
          </div>
          <Button
            appearance="subtle"
            icon={<EditRegular />}
            onClick={() => onEditStep(card.stepIndex)}
            className={styles.editButton}
            aria-label="Edit"
          />
        </div>
      ))}

      {/* Screenshots */}
      <div className={styles.sectionLabel}>Screenshots</div>
      <div className={styles.screenshotRow}>
        {capturing ? (
          <div className={styles.spinnerBox}>
            <Spinner size="tiny" />
            <span>Capturing screenshots...</span>
          </div>
        ) : (
          <>
            {screenshots.viewport && (
              <img
                src={`data:image/jpeg;base64,${screenshots.viewport}`}
                alt="Viewport screenshot"
                className={styles.thumbnail}
              />
            )}
            {screenshots.canvas && (
              <img
                src={`data:image/jpeg;base64,${screenshots.canvas}`}
                alt="Canvas screenshot"
                className={styles.thumbnail}
              />
            )}
            {!screenshots.viewport && !screenshots.canvas && (
              <span style={{ color: tokens.colorNeutralForeground3, fontSize: '14px' }}>
                No screenshots captured
              </span>
            )}
            <Button
              appearance="subtle"
              icon={<ArrowSyncRegular />}
              onClick={onRecapture}
              size="small"
              disabled={capturing}
              aria-label="Recapture"
              data-testid="wizard-recapture"
            >
              Recapture
            </Button>
            {type === 'bug' && onAttachImage && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  aria-label="Attach screenshot image"
                  data-testid="screenshot-file-input"
                  onChange={(e) => void handleAttachFile(e)}
                  style={{ display: 'none' }}
                />
                <Button
                  appearance="subtle"
                  icon={<ImageRegular />}
                  size="small"
                  disabled={capturing || attachingImage}
                  aria-label="Attach image"
                  data-testid="attach-image-button"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {attachingImage ? 'Attaching...' : 'Attach image'}
                </Button>
              </>
            )}
          </>
        )}
      </div>
      {attachError && (
        <p role="alert" style={{ color: 'var(--hts-error-strong)', fontSize: '13px', margin: 0 }}>
          {attachError}
        </p>
      )}

      {/* Expandable environment details */}
      <div
        className={styles.expandableHeader}
        onClick={() => setShowEnvDetails(!showEnvDetails)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setShowEnvDetails(!showEnvDetails);
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={showEnvDetails}
        aria-label="Environment Details"
      >
        {showEnvDetails ? <ChevronUpRegular /> : <ChevronDownRegular />}
        <span style={{ fontWeight: 600, fontSize: '14px' }}>Environment Details</span>
        <span style={{ color: tokens.colorNeutralForeground3, fontSize: '12px', marginLeft: 'auto' }}>
          {environment.deviceType} &middot; {environment.browser}
        </span>
      </div>

      {showEnvDetails && (
        <div className={styles.expandableContent}>
          <table className={styles.envTable}>
            <tbody>
              {envRows.map((row) => (
                <tr key={row.label} className={styles.envRow}>
                  <td className={styles.envLabel}>{row.label}</td>
                  <td className={styles.envValue}>{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
